import { ObjectConfig, required } from "@homebound/form-state";
import {
  baseChangeEventFormConfig,
  BaseChangeEventFormInput,
  baseChangeEventMapToForm,
} from "src/routes/projects/change-events/guided/baseChangeEventForm";
import {
  reconcileImpactedCommitmentsFormConfig,
  ReconcileImpactedCommitmentsFormInput,
  reconcileImpactedCommitmentsMapToForm,
} from "src/routes/projects/change-events/guided/workflows/sharedSteps/ReconcileImpactedCommitmentsStep";
import { GuidedChangeEventPage_ChangeEventFragment } from "../../GuidedChangeEventPage.generated";

/**
 * What the apply-configuration wizard collects.
 *
 * The change event itself only takes the _intent_ -- which reserved config we are applying, and which of
 * its options the user actually wants -- and derives its ready plan, version, checkout config and option
 * rows from that.
 */
export type ApplyConfigurationFormInput = BaseChangeEventFormInput & {
  /** The change event's `ChangeEventConfigChange`, once it has one. */
  configChangeId?: string | null;
  /** The reserved `CheckoutConfig` being applied from. */
  targetConfigId?: string | null;
  /** The options on the config change's `result`. Local-only, since Select Changes saves each pick onto `result` itself. */
  optionIds?: string[] | null;
} & ReconcileImpactedCommitmentsFormInput;

// Exported so the partial workflow and the tests build their form state from the same config.
export const applyConfigurationFormConfig: ObjectConfig<ApplyConfigurationFormInput> = {
  ...baseChangeEventFormConfig,
  configChangeId: { type: "value" },
  targetConfigId: { type: "value", rules: [required] },
  optionIds: {
    type: "value",
    isLocalOnly: true,
    // The options are what actually gets applied, so an empty list means there is nothing to save
    rules: [({ value }) => (value?.nonEmpty ? undefined : "At least one configuration option is required")],
  },
  ...reconcileImpactedCommitmentsFormConfig,
};

/**
 * Rebuilds the form from a saved change event, so edit mode / a mid-wizard refresh comes back to
 * what was already saved.
 *
 * The options come off the config change's `result`, not the change event's proposed options: `result` is
 * the configuration the user composed, and the proposed options are the projection of it.
 */
export function applyConfigurationMapToForm(
  changeEvent: GuidedChangeEventPage_ChangeEventFragment,
): ApplyConfigurationFormInput {
  const { configChange } = changeEvent;
  return {
    ...baseChangeEventMapToForm(changeEvent),
    configChangeId: configChange?.id,
    targetConfigId: configChange?.target?.id,
    optionIds: (configChange?.result?.config.options ?? []).map((o) => o.readyPlanOption.id),
    ...reconcileImpactedCommitmentsMapToForm(changeEvent),
  };
}
